/**
 * BackupSettings - Backup & Restore Einstellungen
 * Extrahiert aus SettingsPage.js für bessere Wartbarkeit
 */
import React, { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Switch } from '@/components/ui/switch';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import { Database, Download, History, Mail, Save, Upload, AlertTriangle, CheckCircle, FileArchive, Lock, Eye, EyeOff } from 'lucide-react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const BackupSettings = () => {
  const [backupSettings, setBackupSettings] = useState({
    enabled: false,
    interval: 'daily',
    backup_hour: 3,
    include_json: true,
    include_csv: true,
    email_recipients: [],
    encryption_enabled: false,
    encryption_password_set: false,
  });
  // Separates State für das Klartext-Passwort, damit es nicht mit dem
  // GET-Response (der nur `encryption_password_set: true|false` liefert)
  // vermischt wird. Leerer String beim Speichern = "Passwort beibehalten".
  const [newPassword, setNewPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [restorePassword, setRestorePassword] = useState('');
  const [backupLogs, setBackupLogs] = useState([]);
  const [loadingBackup, setLoadingBackup] = useState(false);
  const [runningBackup, setRunningBackup] = useState(false);
  const [restoring, setRestoring] = useState(false);
  const [restoreResult, setRestoreResult] = useState(null);
  const [restoreFile, setRestoreFile] = useState(null);
  const [verifying, setVerifying] = useState(false);
  const [verifyingElapsed, setVerifyingElapsed] = useState(0);
  const [integrityResult, setIntegrityResult] = useState(null);

  const fetchBackupSettings = useCallback(async () => {
    try {
      const token = localStorage.getItem('token');
      const [settingsRes, logsRes] = await Promise.all([
        axios.get(`${API}/backup/settings`, {
          headers: { Authorization: `Bearer ${token}` }
        }),
        axios.get(`${API}/backup/logs`, {
          headers: { Authorization: `Bearer ${token}` }
        })
      ]);
      
      if (settingsRes.data) {
        setBackupSettings(settingsRes.data);
      }
      if (logsRes.data?.logs) {
        setBackupLogs(logsRes.data.logs);
      }
    } catch (error) {
      console.error('Error fetching backup settings:', error);
    }
  }, []);

  useEffect(() => {
    fetchBackupSettings();
  }, [fetchBackupSettings]);

  const saveBackupSettings = async () => {
    setLoadingBackup(true);
    try {
      const token = localStorage.getItem('token');
      // Klartext-Passwort nur senden wenn der User es tatsaechlich getippt hat.
      // Ein leerer String bedeutet fuer den Backend "Passwort beibehalten".
      const payload = { ...backupSettings };
      if (newPassword) {
        if (newPassword.length < 8) {
          toast.error('Passwort muss mindestens 8 Zeichen lang sein');
          setLoadingBackup(false);
          return;
        }
        payload.encryption_password = newPassword;
      }
      // Frontend-only Felder rauswerfen, die der Backend nicht erwartet
      delete payload.encryption_password_set;
      delete payload.last_backup;
      delete payload.collections_count;
      delete payload.statistics;
      delete payload.type;

      await axios.put(`${API}/backup/settings`, payload, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Backup-Einstellungen gespeichert');
      setNewPassword('');
      fetchBackupSettings();
    } catch (error) {
      console.error('Error saving backup settings:', error);
      toast.error(error.response?.data?.detail || 'Fehler beim Speichern');
    } finally {
      setLoadingBackup(false);
    }
  };

  // Polling helper.
  // ``onProgress(secondsElapsed)`` wird bei jedem Tick aufgerufen, damit das
  // UI einen Live-Counter anzeigen kann. Axios-Timeout pro Request: 30 s,
  // damit Cloudflare/Ingress-504er (~60 s) das Polling-Interval nicht stauen.
  const pollJobStatus = async (jobId, onDone, onError, onProgress) => {
    const token = localStorage.getItem('token');
    const maxAttempts = 240; // 20 min max (5s intervals) — manche Backups sind gross
    const startedAt = Date.now();
    let warnedSlow = false;
    for (let i = 0; i < maxAttempts; i++) {
      await new Promise(r => setTimeout(r, 5000));
      const elapsed = Math.floor((Date.now() - startedAt) / 1000);
      if (onProgress) onProgress(elapsed);
      // Bei sehr grossen Backups (>2 min) einmalig hinweisen.
      if (!warnedSlow && elapsed > 120) {
        warnedSlow = true;
        toast.info('Bei grossen Datenmengen kann das mehrere Minuten dauern…');
      }
      try {
        const resp = await axios.get(`${API}/backup/job/${jobId}`, {
          headers: { Authorization: `Bearer ${token}` },
          timeout: 30000,
        });
        if (resp.data.status === 'done') { onDone(resp.data); return; }
        if (resp.data.status === 'error') { onError(resp.data.error || 'Unbekannter Fehler'); return; }
      } catch (err) {
        // 504/timeout/network → einfach weiterpollen.
        // Erst nach 20 min geben wir auf.
      }
    }
    onError('Timeout - Backup dauert zu lange');
  };

  const runManualBackup = async (sendEmail = true) => {
    setRunningBackup(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(`${API}/backup/run`, {
        send_email: sendEmail,
        include_json: backupSettings.include_json !== false,
        include_csv: backupSettings.include_csv !== false,
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });

      const jobId = response.data.job_id;
      toast.info('Backup wird erstellt... Bitte warten.');

      pollJobStatus(
        jobId,
        async (job) => {
          if (sendEmail) {
            toast.success(`Backup (${job.size_mb} MB, ${job.collections_count} Collections) per E-Mail gesendet`);
          } else if (job.download_id) {
            // Download the backup
            try {
              const dlResp = await axios.get(`${API}/backup/download/${job.download_id}`, {
                headers: { Authorization: `Bearer ${token}` },
                responseType: 'blob'
              });
              const url = window.URL.createObjectURL(new Blob([dlResp.data]));
              const link = document.createElement('a');
              link.href = url;
              link.setAttribute('download', `backup_${new Date().toISOString().split('T')[0]}.zip`);
              document.body.appendChild(link);
              link.click();
              link.remove();
              toast.success(`Backup heruntergeladen (${job.size_mb} MB)`);
            } catch {
              toast.error('Download fehlgeschlagen');
            }
          }
          setRunningBackup(false);
          fetchBackupSettings();
        },
        (err) => {
          toast.error(`Backup fehlgeschlagen: ${err}`);
          setRunningBackup(false);
        }
      );
    } catch (error) {
      console.error('Backup error:', error);
      toast.error('Fehler beim Starten des Backups');
      setRunningBackup(false);
    }
  };

  const handleRestore = async (dryRun = true) => {
    if (!restoreFile) {
      toast.error('Bitte zuerst eine ZIP-Datei auswählen');
      return;
    }
    setRestoring(true);
    setRestoreResult(null);
    try {
      const token = localStorage.getItem('token');
      const formData = new FormData();
      formData.append('file', restoreFile);
      // Passwort als Query-Param (Backend liest aus Query). Encoded fuer
      // Sonderzeichen wie & oder = im Passwort.
      let url = `${API}/backup/restore?dry_run=${dryRun}`;
      if (restorePassword) {
        url += `&password=${encodeURIComponent(restorePassword)}`;
      }
      const response = await axios.post(
        url,
        formData,
        { headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'multipart/form-data' } }
      );
      setRestoreResult(response.data);
      if (dryRun) {
        toast.success(`Vorschau: ${response.data.summary.restored_count} Collections würden wiederhergestellt`);
      } else {
        toast.success(`${response.data.summary.restored_count} Collections erfolgreich wiederhergestellt`);
        fetchBackupSettings();
      }
    } catch (error) {
      console.error('Restore error:', error);
      toast.error(error.response?.data?.detail || 'Fehler bei der Wiederherstellung');
    } finally {
      setRestoring(false);
    }
  };

  const runIntegrityCheck = async () => {
    setVerifying(true);
    setVerifyingElapsed(0);
    setIntegrityResult(null);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(`${API}/backup/verify-integrity`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });

      const jobId = response.data.job_id;
      toast.info('Integritätsprüfung läuft... Bitte warten.');

      pollJobStatus(
        jobId,
        (job) => {
          const result = job.result || {};
          setIntegrityResult({ success: true, ...result });
          if (result.integrity_ok) {
            toast.success(`Integrität bestätigt (${result.size_mb} MB, ${result.collections_db} Collections)`);
          } else {
            toast.error('Integrität: Probleme gefunden');
          }
          setVerifying(false);
          setVerifyingElapsed(0);
        },
        (err) => {
          toast.error(`Integritätsprüfung fehlgeschlagen: ${err}`);
          setVerifying(false);
          setVerifyingElapsed(0);
        },
        (elapsed) => setVerifyingElapsed(elapsed)
      );
    } catch (error) {
      console.error('Integrity check error:', error);
      toast.error('Fehler beim Starten der Integritätsprüfung');
      setVerifying(false);
      setVerifyingElapsed(0);
    }
  };

  // Formatiert Sekunden als mm:ss (z. B. 83 → "1:23").
  const formatElapsed = (s) => {
    const m = Math.floor(s / 60);
    const r = s % 60;
    return `${m}:${String(r).padStart(2, '0')}`;
  };

  return (
    <Card>
      <CardHeader>
        <div className="flex justify-between items-center">
          <div>
            <CardTitle className="flex items-center gap-2">
              <Database className="h-6 w-6 text-green-600" />
              Backup-Einstellungen
            </CardTitle>
            <CardDescription>
              Automatische Datensicherung per E-Mail
            </CardDescription>
          </div>
        </div>
      </CardHeader>
      <CardContent className="space-y-6">
        {/* Aktivierung */}
        <div className="p-4 bg-green-50 rounded-lg border border-green-200">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <Database className="h-8 w-8 text-green-600" />
              <div>
                <h3 className="font-semibold text-green-900">Automatisches Backup</h3>
                <p className="text-sm text-green-700">
                  {backupSettings.enabled 
                    ? backupSettings.interval === '6hours' 
                      ? 'Alle 6 Stunden aktiv' 
                      : `Täglich um ${backupSettings.backup_hour}:00 Uhr aktiv`
                    : 'Deaktiviert'}
                </p>
              </div>
            </div>
            <Switch
              checked={backupSettings.enabled}
              onCheckedChange={(checked) => setBackupSettings({...backupSettings, enabled: checked})}
            />
          </div>
        </div>

        {/* Intervall */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <Label>Backup-Intervall</Label>
            <Select
              value={backupSettings.interval}
              onValueChange={(value) => setBackupSettings({...backupSettings, interval: value})}
            >
              <SelectTrigger className="mt-1">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="daily">Täglich</SelectItem>
                <SelectItem value="6hours">Alle 6 Stunden</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {backupSettings.interval === 'daily' && (
            <div>
              <Label>Uhrzeit (Stunde)</Label>
              <Select
                value={String(backupSettings.backup_hour)}
                onValueChange={(value) => setBackupSettings({...backupSettings, backup_hour: parseInt(value)})}
              >
                <SelectTrigger className="mt-1">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {[0,1,2,3,4,5,6,7,8,9,10,11,12,13,14,15,16,17,18,19,20,21,22,23].map(h => (
                    <SelectItem key={h} value={String(h)}>{h}:00 Uhr</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}
        </div>

        {/* Format */}
        <div>
          <Label className="mb-2 block">Backup-Format</Label>
          <div className="flex gap-4">
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="checkbox"
                checked={backupSettings.include_json}
                onChange={(e) => setBackupSettings({...backupSettings, include_json: e.target.checked})}
                className="rounded"
              />
              <span>JSON (für Wiederherstellung)</span>
            </label>
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="checkbox"
                checked={backupSettings.include_csv}
                onChange={(e) => setBackupSettings({...backupSettings, include_csv: e.target.checked})}
                className="rounded"
              />
              <span>CSV (Excel-kompatibel)</span>
            </label>
          </div>
        </div>

        {/* Enthaltene Daten Info */}
        <div className="p-4 bg-green-50 rounded-lg border border-green-200">
          <h4 className="font-medium mb-2 flex items-center gap-2">
            <CheckCircle className="h-4 w-4 text-green-600" />
            Vollständiges Backup: {backupSettings.collections_count || '...'} Collections
          </h4>
          <p className="text-sm text-green-700 mb-2">
            Alle Daten werden automatisch gesichert - auch neue Features.
          </p>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-1 text-xs text-green-800">
            <span>Kunden & Abos</span>
            <span>Termine & Buchungen</span>
            <span>Rechnungen & Transaktionen</span>
            <span>Ernährung & Rezepte</span>
            <span>Training & Körperdaten</span>
            <span>Tarif 595 & Versicherung</span>
            <span>Win-Back & Rabattcodes</span>
            <span>WhatsApp & E-Mail Templates</span>
            <span>Alle Einstellungen</span>
          </div>
        </div>

        {/* Verschlüsselung */}
        <div className="p-4 bg-amber-50 rounded-lg border border-amber-200 space-y-3" data-testid="backup-encryption-card">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <Lock className="h-7 w-7 text-amber-700" />
              <div>
                <h3 className="font-semibold text-amber-900">AES-256 Verschlüsselung</h3>
                <p className="text-sm text-amber-700">
                  {backupSettings.encryption_enabled
                    ? backupSettings.encryption_password_set
                      ? 'Aktiv — Backup-ZIPs sind passwortgeschützt'
                      : 'Aktiviert, aber noch kein Passwort gesetzt!'
                    : 'Deaktiviert — Backup-ZIPs sind unverschlüsselt'}
                </p>
              </div>
            </div>
            <Switch
              checked={!!backupSettings.encryption_enabled}
              onCheckedChange={(checked) => setBackupSettings({...backupSettings, encryption_enabled: checked})}
              data-testid="backup-encryption-toggle"
            />
          </div>

          {backupSettings.encryption_enabled && (
            <div className="space-y-2 pt-2 border-t border-amber-200">
              <Label className="text-sm">
                {backupSettings.encryption_password_set ? 'Neues Passwort (leer lassen = beibehalten)' : 'Passwort festlegen (min. 8 Zeichen)'}
              </Label>
              <div className="flex gap-2 items-center">
                <div className="relative flex-1">
                  <input
                    type={showPassword ? 'text' : 'password'}
                    value={newPassword}
                    onChange={(e) => setNewPassword(e.target.value)}
                    placeholder={backupSettings.encryption_password_set ? '•••••••• (gesetzt)' : 'Passwort eingeben'}
                    className="w-full px-3 py-2 pr-10 border border-amber-300 rounded text-sm focus:outline-none focus:ring-2 focus:ring-amber-500"
                    data-testid="backup-encryption-password-input"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(s => !s)}
                    className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-700"
                    data-testid="backup-encryption-password-toggle"
                  >
                    {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                  </button>
                </div>
              </div>
              <div className="text-xs text-amber-800 bg-amber-100 rounded p-2 flex items-start gap-2">
                <AlertTriangle className="h-4 w-4 flex-shrink-0 mt-0.5" />
                <span>
                  Bewahre das Passwort an einem sicheren Ort auf (Passwort-Manager).
                  Ohne dieses Passwort kannst du das Backup <strong>nicht</strong> wiederherstellen.
                  E-Mail-Empfänger erhalten das Passwort <strong>nicht</strong> mit dem Backup.
                </span>
              </div>
            </div>
          )}
        </div>

        {/* Speichern Button */}
        <Button 
          onClick={saveBackupSettings}
          disabled={loadingBackup}
          className="bg-green-600 hover:bg-green-700"
        >
          <Save className="h-4 w-4 mr-2" />
          {loadingBackup ? 'Wird gespeichert...' : 'Einstellungen speichern'}
        </Button>

        {/* Manuelles Backup */}
        <div className="border-t pt-6 mt-6">
          <h4 className="font-semibold mb-3 flex items-center gap-2">
            <Download className="h-5 w-5" />
            Manuelles Backup
          </h4>
          <div className="flex gap-3">
            <Button 
              onClick={() => runManualBackup(true)}
              disabled={runningBackup}
              className="bg-blue-600 hover:bg-blue-700"
              data-testid="backup-email-btn"
            >
              <Mail className="h-4 w-4 mr-2" />
              {runningBackup ? 'Wird erstellt...' : 'Backup per E-Mail senden'}
            </Button>
            <Button 
              onClick={() => runManualBackup(false)}
              disabled={runningBackup}
              variant="outline"
              data-testid="backup-download-btn"
            >
              <Download className="h-4 w-4 mr-2" />
              Direkt herunterladen
            </Button>
          </div>
        </div>

        {/* Integritätsprüfung */}
        <div className="border-t pt-6 mt-6">
          <h4 className="font-semibold mb-3 flex items-center gap-2">
            <CheckCircle className="h-5 w-5 text-blue-600" />
            Integritätsprüfung
          </h4>
          <p className="text-sm text-gray-600 mb-3">
            Erstellt ein Backup und prüft, ob alle Daten korrekt exportiert wurden. Läuft automatisch bei jedem geplanten Backup.
          </p>
          <Button
            onClick={runIntegrityCheck}
            disabled={verifying}
            className="bg-blue-600 hover:bg-blue-700"
            data-testid="integrity-check-btn"
          >
            <CheckCircle className="h-4 w-4 mr-2" />
            {verifying
              ? `Wird geprüft… ${formatElapsed(verifyingElapsed)}`
              : 'Jetzt prüfen'}
          </Button>
          {verifying && (
            <p className="mt-2 text-xs text-gray-500" data-testid="integrity-progress-hint">
              Backup wird erstellt, verschlüsselt und gegen die DB geprüft.
              {verifyingElapsed > 60 && ' Bei grossen Datenmengen kann das mehrere Minuten dauern.'}
            </p>
          )}
          {integrityResult && (
            <div className={`mt-3 p-4 rounded-lg border ${integrityResult.integrity_ok
              ? 'bg-green-50 border-green-200' : 'bg-red-50 border-red-200'}`}>
              <div className="flex items-center gap-2 mb-2">
                {integrityResult.integrity_ok
                  ? <CheckCircle className="h-5 w-5 text-green-600" />
                  : <AlertTriangle className="h-5 w-5 text-red-600" />}
                <span className="font-semibold">
                  {integrityResult.integrity_ok ? 'Backup ist vollständig und korrekt' : 'Probleme gefunden'}
                </span>
              </div>
              <div className="text-sm space-y-1">
                <p>DB: {integrityResult.collections_db} Collections / ZIP: {integrityResult.collections_zip} Collections</p>
                <p>Grösse: {integrityResult.size_mb} MB</p>
                {integrityResult.warnings?.length > 0 && (
                  <div className="mt-2">
                    <p className="font-medium text-orange-700">Warnungen:</p>
                    {integrityResult.warnings.map((w, i) => (
                      <p key={i} className="text-orange-600 ml-2">- {w}</p>
                    ))}
                  </div>
                )}
                {integrityResult.mismatches?.length > 0 && (
                  <div className="mt-2">
                    <p className="font-medium text-orange-700">Anzahl-Abweichungen:</p>
                    {integrityResult.mismatches.slice(0, 5).map((m, i) => (
                      <p key={i} className="text-orange-600 ml-2 text-xs">
                        {m.collection}: DB={m.db_count}, ZIP={m.zip_count}
                      </p>
                    ))}
                  </div>
                )}
              </div>
            </div>
          )}
        </div>

        {/* Wiederherstellen */}
        <div className="border-t pt-6 mt-6">
          <h4 className="font-semibold mb-3 flex items-center gap-2">
            <Upload className="h-5 w-5 text-orange-600" />
            Wiederherstellen (Restore)
          </h4>
          <p className="text-sm text-gray-600 mb-3">
            ZIP-Datei aus Backup-E-Mail oder Download hochladen, um alle Daten wiederherzustellen.
          </p>
          <div className="p-4 bg-orange-50 rounded-lg border border-orange-200 space-y-3">
            <div className="flex items-center gap-3">
              <label className="flex-1">
                <input
                  type="file"
                  accept=".zip"
                  onChange={(e) => { setRestoreFile(e.target.files[0]); setRestoreResult(null); }}
                  className="block w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded file:border-0 file:text-sm file:font-semibold file:bg-orange-100 file:text-orange-700 hover:file:bg-orange-200"
                  data-testid="restore-file-input"
                />
              </label>
            </div>
            {restoreFile && (
              <div className="flex items-center gap-2 text-sm">
                <FileArchive className="h-4 w-4 text-orange-600" />
                <span>{restoreFile.name} ({(restoreFile.size / 1024 / 1024).toFixed(1)} MB)</span>
              </div>
            )}
            {/* Optionales Passwort fuer verschluesselte ZIPs. Leer lassen
                wenn Backup unverschluesselt ODER mit dem im System
                gespeicherten Default-Passwort erstellt wurde. */}
            <div>
              <Label className="text-xs text-orange-800 flex items-center gap-1.5">
                <Lock className="h-3 w-3" />
                Passwort (nur falls ZIP verschlüsselt — leer lassen für Standard)
              </Label>
              <input
                type="password"
                value={restorePassword}
                onChange={(e) => setRestorePassword(e.target.value)}
                placeholder="Optional"
                className="mt-1 w-full px-3 py-2 border border-orange-300 rounded text-sm focus:outline-none focus:ring-2 focus:ring-orange-500"
                data-testid="restore-password-input"
              />
            </div>
            <div className="flex gap-3">
              <Button
                onClick={() => handleRestore(true)}
                disabled={restoring || !restoreFile}
                variant="outline"
                className="border-orange-300 text-orange-700 hover:bg-orange-100"
                data-testid="restore-preview-btn"
              >
                {restoring ? 'Wird geprüft...' : 'Vorschau (kein Schreibzugriff)'}
              </Button>
              <Button
                onClick={() => {
                  if (window.confirm('ACHTUNG: Alle bestehenden Daten werden durch das Backup ERSETZT. Fortfahren?')) {
                    handleRestore(false);
                  }
                }}
                disabled={restoring || !restoreFile}
                className="bg-orange-600 hover:bg-orange-700"
                data-testid="restore-execute-btn"
              >
                <AlertTriangle className="h-4 w-4 mr-2" />
                {restoring ? 'Wird wiederhergestellt...' : 'Jetzt wiederherstellen'}
              </Button>
            </div>
            {restoreResult && (
              <div className={`p-3 rounded text-sm ${restoreResult.dry_run ? 'bg-blue-50 border-blue-200' : 'bg-green-50 border-green-200'} border`}>
                <p className="font-medium mb-1">
                  {restoreResult.dry_run ? 'Vorschau-Ergebnis:' : 'Wiederherstellung abgeschlossen:'}
                </p>
                <p>{restoreResult.summary.restored_count} Collections {restoreResult.dry_run ? 'würden wiederhergestellt' : 'wiederhergestellt'}</p>
                {restoreResult.summary.error_count > 0 && (
                  <p className="text-red-600">{restoreResult.summary.error_count} Fehler</p>
                )}
              </div>
            )}
          </div>
        </div>

        {/* Letztes Backup */}
        {backupLogs.length > 0 && (
          <div className="border-t pt-6 mt-6">
            <h4 className="font-semibold mb-3 flex items-center gap-2">
              <History className="h-5 w-5" />
              Letztes Backup
            </h4>
            <div className="p-3 bg-gray-100 rounded-lg">
              {backupLogs.map((log, idx) => (
                <div key={idx} className="flex justify-between items-center">
                  <div>
                    <span className="text-sm">
                      {new Date(log.timestamp).toLocaleString('de-CH')}
                    </span>
                    {log.size_bytes && (
                      <span className="text-xs text-gray-500 ml-2">
                        ({(log.size_bytes / 1024 / 1024).toFixed(2)} MB)
                      </span>
                    )}
                  </div>
                  <Badge className={log.status === 'success' ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}>
                    {log.status === 'success' ? '✅ Erfolgreich' : '❌ Fehlgeschlagen'}
                  </Badge>
                </div>
              ))}
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default BackupSettings;
